/** docs/UX.md 12 · `/m/materials` 素材库 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AI_TEST_COLLECTION,
  AI_TEST_RETIRED_COLLECTIONS,
  AI_TEST_TITLE,
  AI_TEST_LEVELS,
  AI_TEST_PART_RANGE_LABEL,
  confusionLabel,
  DEFAULT_COLLECTION,
  DOMAINS,
  MATERIAL_KINDS,
  TASKS,
} from "@xinghai/shared";
import type { MaterialImportPayload } from "@xinghai/shared";
import {
  Button,
  EmptyState,
  Field,
  Icon,
  Select,
  Sheet,
  Table,
  useConfirmSafe,
  useToast,
} from "@xinghai/ui";
import type { Column } from "@xinghai/ui";
import { materialsApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import {
  ErrorNotice,
  KeyValue,
  LoadingBlock,
  PageHeader,
  QueryBoundary,
  StatusTag,
} from "./common";
import { DOMAIN_LABEL, MATERIAL_KIND_LABEL, TASK_LABEL } from "./labels";
import { parseJsonInput, readTextFile, validateMaterialImport } from "./importValidation";
import type { ImportIssue } from "./importValidation";
import type { MaterialSummary } from "./types";

interface Filters {
  kind: string;
  task: string;
  domain: string;
  confusion: string;
  /** `corpus_v1` | `ai_test_v1` | "" for all. */
  collection: string;
  /** 《AI 正音测试》level as a string; "" for all. */
  level: string;
}

const EMPTY: Filters = {
  kind: "",
  task: "",
  domain: "",
  confusion: "",
  collection: "",
  level: "",
};

/** Collections the admin screen knows how to name. */
export const COLLECTION_LABEL: Record<string, string> = {
  [DEFAULT_COLLECTION]: "P0 语料库",
  [AI_TEST_COLLECTION]: AI_TEST_TITLE,
  ...Object.fromEntries(
    AI_TEST_RETIRED_COLLECTIONS.map((c, i) => [c, `${AI_TEST_TITLE} v${i + 1}（已退役）`]),
  ),
};

export function MaterialsPage() {
  const toast = useToast();
  const confirm = useConfirmSafe();
  const qc = useQueryClient();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const serverFilters = useMemo(
    () => ({
      ...(filters.kind ? { kind: filters.kind } : {}),
      ...(filters.task ? { task: filters.task as (typeof TASKS)[number] } : {}),
      ...(filters.domain ? { domain: filters.domain as (typeof DOMAINS)[number] } : {}),
      ...(filters.collection ? { collection: filters.collection } : {}),
      ...(filters.level ? { level: Number(filters.level) } : {}),
    }),
    [filters.kind, filters.task, filters.domain, filters.collection, filters.level],
  );

  const list = useQuery({
    queryKey: qk.materials(serverFilters),
    queryFn: ({ signal }) => materialsApi.list(serverFilters, signal),
  });

  const rows = useMemo(() => {
    const all = list.data?.materials ?? [];
    const c = filters.confusion.trim();
    if (!c) return all;
    return all.filter((m) => m.confusion_classes.some((x) => x.includes(c)));
  }, [list.data, filters.confusion]);

  const confusionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const m of list.data?.materials ?? []) for (const c of m.confusion_classes) set.add(c);
    return [...set].sort();
  }, [list.data]);

  const freeze = useMutation({
    mutationFn: ({ id, version }: { id: string; version: string }) =>
      materialsApi.freeze(id, version),
    onSuccess: (r) => {
      toast.success(`素材 ${r.material.id} 已冻结为 v${r.version}`);
      void qc.invalidateQueries({ queryKey: ["materials"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const columns: Array<Column<MaterialSummary>> = [
    {
      key: "id",
      header: "编号",
      render: (m) => <span className="xz-adm__mono">{m.id}</span>,
      width: "11rem",
    },
    {
      key: "title",
      header: "标题",
      render: (m) => (
        <span>
          {m.title}
          {m.rights === "in_copyright" && (
            <span title={m.rights_note ?? "歌词仍在著作权保护期，商用前需取得授权"}>
              <StatusTag icon="ui-warning" tone="warning">
                著作权
              </StatusTag>
            </span>
          )}
        </span>
      ),
    },
    {
      key: "collection",
      header: "素材集",
      hideOnMobile: true,
      render: (m) =>
        m.collection === AI_TEST_COLLECTION ? (
          <span>
            {COLLECTION_LABEL[m.collection]}
            {m.level ? ` · ${AI_TEST_PART_RANGE_LABEL[m.level] ?? `第 ${m.level} 部分`}` : ""}
            {m.section ? <span className="gz-caption"> {m.section}</span> : null}
          </span>
        ) : (
          (COLLECTION_LABEL[m.collection] ?? m.collection)
        ),
    },
    { key: "kind", header: "类型", render: (m) => MATERIAL_KIND_LABEL[m.kind] },
    { key: "task", header: "任务", render: (m) => TASK_LABEL[m.task] },
    { key: "domain", header: "域", render: (m) => DOMAIN_LABEL[m.domain] },
    {
      key: "confusion",
      header: "混淆类",
      hideOnMobile: true,
      render: (m) =>
        m.confusion_classes.length === 0 ? (
          <span className="gz-muted">—</span>
        ) : (
          <span className="xz-adm__tags">
            {m.confusion_classes.map((c) => (
              <StatusTag key={c} icon="ui-tag">
                {confusionLabel(c)}
              </StatusTag>
            ))}
          </span>
        ),
    },
    { key: "chars", header: "字数", align: "end", render: (m) => m.char_count },
    {
      key: "frozen",
      header: "状态",
      render: (m) =>
        m.frozen ? (
          <StatusTag icon="ui-lock" tone="success">
            已冻结
          </StatusTag>
        ) : (
          <StatusTag icon="ui-edit">可编辑</StatusTag>
        ),
    },
    {
      key: "actions",
      header: "操作",
      render: (m) => (
        <span className="xz-adm__rowactions">
          <Button size="sm" variant="secondary" onClick={() => setDetailId(m.id)}>
            详情
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="xz-adm">
      <PageHeader
        title="素材库"
        description="锁定拼音是入库前置（红线 14.5）。冻结之后素材不可再改。"
        actions={
          <Button
            leadingIcon={<Icon id="ui-upload" size={16} />}
            onClick={() => setImportOpen(true)}
          >
            导入素材
          </Button>
        }
      />

      <div className="xz-adm__toolbar">
        <Field label="素材集">
          <Select
            value={filters.collection}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                collection: e.target.value,
                // level only means anything inside 《AI 正音测试》
                level: e.target.value === AI_TEST_COLLECTION ? f.level : "",
              }))
            }
            options={[
              { value: "", label: "全部素材集" },
              { value: DEFAULT_COLLECTION, label: COLLECTION_LABEL[DEFAULT_COLLECTION]! },
              { value: AI_TEST_COLLECTION, label: COLLECTION_LABEL[AI_TEST_COLLECTION]! },
            ]}
          />
        </Field>
        <Field label="部分">
          <Select
            value={filters.level}
            disabled={filters.collection !== AI_TEST_COLLECTION}
            onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))}
            options={[
              { value: "", label: "全部部分" },
              ...AI_TEST_LEVELS.map((l) => ({
                value: String(l),
                label: AI_TEST_PART_RANGE_LABEL[l] ?? `第 ${l} 部分`,
              })),
            ]}
          />
        </Field>
        <Field label="类型">
          <Select
            value={filters.kind}
            onChange={(e) => setFilters((f) => ({ ...f, kind: e.target.value }))}
            options={[
              { value: "", label: "全部类型" },
              ...MATERIAL_KINDS.map((k) => ({ value: k, label: MATERIAL_KIND_LABEL[k] })),
            ]}
          />
        </Field>
        <Field label="任务">
          <Select
            value={filters.task}
            onChange={(e) => setFilters((f) => ({ ...f, task: e.target.value }))}
            options={[
              { value: "", label: "全部任务" },
              ...TASKS.map((t) => ({ value: t, label: TASK_LABEL[t] })),
            ]}
          />
        </Field>
        <Field label="域">
          <Select
            value={filters.domain}
            onChange={(e) => setFilters((f) => ({ ...f, domain: e.target.value }))}
            options={[
              { value: "", label: "全部域" },
              ...DOMAINS.map((d) => ({ value: d, label: DOMAIN_LABEL[d] })),
            ]}
          />
        </Field>
        <Field label="混淆类">
          <Select
            value={filters.confusion}
            onChange={(e) => setFilters((f) => ({ ...f, confusion: e.target.value }))}
            options={[
              { value: "", label: "全部混淆类" },
              ...confusionOptions.map((c) => ({ value: c, label: c })),
            ]}
          />
        </Field>
        <div className="xz-adm__toolbar-end">
          <Button variant="ghost" onClick={() => setFilters(EMPTY)}>
            清除筛选
          </Button>
        </div>
      </div>

      <QueryBoundary
        isPending={list.isPending}
        isError={list.isError}
        error={list.error}
        onRetry={() => void list.refetch()}
      >
        <>
          <p className="gz-caption">共 {rows.length} 条素材</p>
          <Table
            caption="素材库"
            columns={columns}
            rows={rows}
            rowKey={(m) => m.id}
            empty={
              <EmptyState
                title="没有符合条件的素材"
                description="换个筛选条件，或先导入一批素材。"
              />
            }
          />
        </>
      </QueryBoundary>

      {detailId && (
        <MaterialDetailSheet
          id={detailId}
          onClose={() => setDetailId(null)}
          onFreeze={async (m) => {
            const ok = await confirm({
              title: `冻结素材 ${m.id}？`,
              message: "冻结之后这条素材不可再修改，也不能再被导入覆盖。该操作不可撤销。",
              confirmLabel: "冻结",
              danger: true,
            });
            if (!ok) return;
            await freeze.mutateAsync({ id: m.id, version: `v${m.version + 1}` });
            setDetailId(null);
          }}
          busy={freeze.isPending}
        />
      )}

      <MaterialImportSheet open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

// --------------------------------------------------------------------------- //
// detail
// --------------------------------------------------------------------------- //

function MaterialDetailSheet({
  id,
  onClose,
  onFreeze,
  busy,
}: {
  id: string;
  onClose: () => void;
  onFreeze: (m: { id: string; version: number }) => void | Promise<void>;
  busy: boolean;
}) {
  const q = useQuery({ queryKey: qk.material(id), queryFn: () => materialsApi.get(id) });
  const m = q.data?.material;

  return (
    <Sheet
      open
      onClose={onClose}
      title={`素材 ${id}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            关闭
          </Button>
          {m && !m.frozen && (
            <Button variant="danger" loading={busy} onClick={() => void onFreeze(m)}>
              冻结这条素材
            </Button>
          )}
        </>
      }
    >
      {q.isPending ? (
        <LoadingBlock rows={2} label="正在加载素材详情" />
      ) : q.isError || !m ? (
        <ErrorNotice error={q.error} onRetry={() => void q.refetch()} title="素材详情没能加载" />
      ) : (
        <div className="xz-stack">
          <KeyValue
            items={[
              { key: "title", label: "标题", value: m.title },
              { key: "kind", label: "类型", value: MATERIAL_KIND_LABEL[m.kind] },
              { key: "task", label: "任务", value: TASK_LABEL[m.task] },
              { key: "domain", label: "域", value: DOMAIN_LABEL[m.domain] },
              { key: "version", label: "版本", value: `v${m.version}` },
              {
                key: "frozen",
                label: "状态",
                value: m.frozen ? (
                  <StatusTag icon="ui-lock" tone="success">
                    已冻结
                  </StatusTag>
                ) : (
                  <StatusTag icon="ui-edit">可编辑</StatusTag>
                ),
              },
            ]}
          />

          <div>
            <h3 className="gz-type-heading">正文</h3>
            <p style={{ lineHeight: "var(--gz-lh-reading)" }}>{m.text}</p>
            {m.prompt && <p className="gz-muted">提示：{m.prompt}</p>}
          </div>

          {m.confusion_classes.length > 0 && (
            <div>
              <h3 className="gz-type-heading">混淆类</h3>
              <p className="xz-adm__tags">
                {m.confusion_classes.map((c) => (
                  <StatusTag key={c} icon="ui-tag">
                    {confusionLabel(c)}
                  </StatusTag>
                ))}
              </p>
            </div>
          )}

          <div>
            <h3 className="gz-type-heading">逐字（{m.chars.length}）</h3>
            <ul className="xz-chars">
              {m.chars.map((ch, i) => (
                <li key={`${ch.char}-${i}`} className="xz-chars__item">
                  <span className="xz-chars__char">{ch.char}</span>
                  <span className="xz-chars__py">{ch.pinyin_locked}</span>
                  {ch.zhe && <span className="xz-chars__py">辙 {ch.zhe}</span>}
                  {ch.confusion_risk.length > 0 && (
                    <span className="xz-adm__tags">
                      {ch.confusion_risk.map((r) => (
                        <StatusTag key={r} icon="ui-warning" tone="warning">
                          {confusionLabel(r)}
                        </StatusTag>
                      ))}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Sheet>
  );
}

// --------------------------------------------------------------------------- //
// import
// --------------------------------------------------------------------------- //

export function IssueList({ issues }: { issues: ImportIssue[] }) {
  return (
    <ul className="xz-issues" aria-label="导入校验错误">
      {issues.map((it, i) => (
        <li key={i} className="xz-issues__item">
          <Icon id="ui-error" size={16} />
          <span className="xz-issues__where">
            第 {it.row} 行{it.id ? ` · ${it.id}` : ""}
            {it.char ? ` · 「${it.char}」` : ""}
            {it.charIndex !== undefined ? `（第 ${it.charIndex + 1} 字）` : ""}
          </span>
          <span>{it.message}</span>
        </li>
      ))}
    </ul>
  );
}

function MaterialImportSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [issues, setIssues] = useState<ImportIssue[]>([]);
  const [payload, setPayload] = useState<MaterialImportPayload | null>(null);
  const [count, setCount] = useState(0);
  const [overwrite, setOverwrite] = useState(false);

  const reset = () => {
    setFileName(null);
    setParseError(null);
    setIssues([]);
    setPayload(null);
    setCount(0);
  };

  const run = useMutation({
    mutationFn: (p: MaterialImportPayload) => materialsApi.import(p),
    onSuccess: (r) => {
      toast.success(`已导入 ${r.imported} 条素材`);
      void qc.invalidateQueries({ queryKey: ["materials"] });
      reset();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onFile = async (file: File | null) => {
    reset();
    if (!file) return;
    setFileName(file.name);
    let text: string;
    try {
      text = await readTextFile(file);
    } catch (err) {
      setParseError(`读取文件失败：${(err as Error).message}`);
      return;
    }
    const { value, error } = parseJsonInput(text);
    if (error) {
      setParseError(error);
      return;
    }
    const result = validateMaterialImport(value);
    setIssues(result.issues);
    setPayload(result.payload);
    setCount(result.count);
  };

  const canImport = payload !== null && issues.length === 0 && parseError === null;

  return (
    <Sheet
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title="导入素材"
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            取消
          </Button>
          <Button
            disabled={!canImport}
            loading={run.isPending}
            onClick={() => payload && run.mutate({ ...payload, overwrite })}
          >
            导入
          </Button>
        </>
      }
    >
      <div className="xz-form">
        <Field
          label="素材文件（JSON 数组）"
          hint="可以是 [ …素材… ] 数组，也可以是 { materials: [ … ] }。导入前会用 materialImportSchema 校验，并逐字解析锁定拼音。"
        >
          <input
            type="file"
            accept="application/json,.json"
            aria-label="选择素材 JSON 文件"
            onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
          />
        </Field>

        <label className="xz-form__choice">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
          />
          <span>
            <span>覆盖同编号的已有素材</span>
            <span className="gz-caption">已冻结的素材无论如何都不会被覆盖。</span>
          </span>
        </label>

        {fileName && <p className="gz-caption">已选择：{fileName}</p>}

        {parseError && (
          <div className="gz-notice gz-notice--error" role="alert">
            <Icon id="ui-error" size={20} />
            <span>{parseError}</span>
          </div>
        )}

        {issues.length > 0 && (
          <div>
            <h3 className="gz-type-heading">{issues.length} 处问题，先改好才能导入</h3>
            <IssueList issues={issues} />
          </div>
        )}

        {canImport && (
          <div className="gz-notice gz-notice--success" role="status">
            <Icon id="ui-check-circle" size={20} />
            <span>校验通过，共 {count} 条素材可以导入。</span>
          </div>
        )}
      </div>
    </Sheet>
  );
}
